/**
 * Hala CRM - Admin Settings Page
 * System Control: System settings and configuration
 * Access: Admin ONLY
 */

import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Settings, Shield, Bell, Clock, AlertCircle, CheckCircle, Info
} from 'lucide-react';

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

function SettingCard({ icon: Icon, title, description, children }) {
  return (
    <div style={{
      backgroundColor: COLORS.white,
      borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
      padding: '20px',
      marginBottom: '16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '10px',
          backgroundColor: COLORS.bgDark,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={22} color={COLORS.primary} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
            {title}
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: COLORS.textMuted }}>
            {description}
          </p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function CRMAdminSettingsPage() {
  const { user } = useOutletContext();
  const [delayHours, setDelayHours] = useState(24);

  // Check access
  const hasAccess = user?.role_id === 'admin';

  // Access denied for non-admins
  if (!hasAccess) {
    return (
      <div style={{ 
        direction: 'rtl', 
        padding: '60px', 
        textAlign: 'center',
        backgroundColor: COLORS.errorLight,
        borderRadius: '12px',
        margin: '40px',
      }}>
        <AlertCircle size={48} color={COLORS.error} style={{ marginBottom: '16px' }} />
        <h2 style={{ color: COLORS.error, margin: '0 0 8px' }}>غير مصرح بالوصول</h2>
        <p style={{ color: COLORS.textMuted, margin: 0 }}>هذه الصفحة متاحة لمدراء النظام فقط</p>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
          إعدادات النظام
        </h1>
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
          تكوين إعدادات نظام CRM
        </p>
      </div>

      {/* Info Banner */}
      <div style={{
        backgroundColor: COLORS.infoLight,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <Info size={20} color={COLORS.info} />
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.info }}>
          الإعدادات هنا تؤثر على سلوك النظام بشكل عام. تأكد من مراجعة التغييرات قبل الحفظ.
        </p>
      </div>

      {/* Settings */}
      <SettingCard
        icon={Clock}
        title="مؤشر التأخير"
        description="الوقت بالساعات الذي يُعتبر بعده الطلب متأخراً"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <input
            type="number"
            value={delayHours}
            onChange={(e) => setDelayHours(parseInt(e.target.value) || 24)}
            min="1"
            max="168"
            style={{
              width: '100px',
              padding: '10px 14px',
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              fontSize: '14px',
              textAlign: 'center',
            }}
          />
          <span style={{ color: COLORS.textMuted, fontSize: '14px' }}>ساعة</span>
        </div>
        <p style={{ margin: '12px 0 0', fontSize: '12px', color: COLORS.textMuted }}>
          القيمة الافتراضية: 24 ساعة. الطلبات الأقدم من هذا الوقت ستظهر باللون الأحمر.
        </p>
      </SettingCard>

      <SettingCard
        icon={Bell}
        title="الإشعارات"
        description="إعدادات إشعارات النظام"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
            <span style={{ fontSize: '14px', color: COLORS.text }}>إشعار عند وصول طلب جديد</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px' }} />
            <span style={{ fontSize: '14px', color: COLORS.text }}>إشعار عند تأخر طلب</span>
          </label>
        </div>
      </SettingCard>

      <SettingCard
        icon={Shield}
        title="الأدوار والصلاحيات"
        description="فهم الأدوار في النظام"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ padding: '12px', backgroundColor: COLORS.bgDark, borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.error }}>مدير النظام (Admin)</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
              تحكم بالنظام فقط - لا يعالج الطلبات
            </p>
          </div>
          <div style={{ padding: '12px', backgroundColor: COLORS.bgDark, borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.primary }}>المشرف (Supervisor)</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
              مراقبة وإدارة الطلبات - إعادة التعيين
            </p>
          </div>
          <div style={{ padding: '12px', backgroundColor: COLORS.bgDark, borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.gold }}>المستشار (Consultant)</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
              يستلم الطلبات مباشرة - تخصص محدد
            </p>
          </div>
          <div style={{ padding: '12px', backgroundColor: COLORS.bgDark, borderRadius: '8px' }}>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.success }}>مستخدم المجموعة (Group User)</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
              يسحب الطلبات من المجمع - تنفيذ
            </p>
          </div>
        </div>
      </SettingCard>

      {/* System Status */}
      <div style={{
        backgroundColor: COLORS.successLight,
        borderRadius: '12px',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <CheckCircle size={20} color={COLORS.success} />
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.success }}>
          النظام يعمل بشكل طبيعي
        </p>
      </div>
    </div>
  );
}
